import React, { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import ChipItem from './ChipItem/ChipItem';
import ElementDropdown from './ElementDropdown/ElementDropdown';
import AddElementButton from './AddElementButton/AddElementButton';
import { sortElementsByName } from './utils';
import styles from './ChipSelector.module.scss';

export interface Element<T = string | number> {
    id: T;
    name: string;
    img?: string;
    description?: string;
}

export interface ChipSelectorProps<T = string | number> {
    elements: Array<Element<T>>;
    selectedElements: Array<Element<T>>;
    changeSelection: (elements: Array<Element<T>>) => void;
    searchLabel?: string;
    allLabel?: string;
    title?: string;
    id?: string;
}

function isElementSelected<T>(selectedElements: Array<Element<T>>, elementId: T): boolean {
    return selectedElements.some(element => element.id === elementId);
}

export function ChipSelector<T extends string | number>(props: ChipSelectorProps<T>): React.ReactElement {
    const [showElementList, setShowElementList] = useState(false);
    const [dropdownPlacement, setDropdownPlacement] = useState<'top' | 'bottom'>('bottom');
    const triggerRef = useRef<HTMLDivElement>(null);

    const searchLabel = props.searchLabel ?? 'Search';
    const allLabel = props.allLabel ?? 'All';

    const availableElements = useMemo(
        () =>
            sortElementsByName(
                props.elements.filter(element => !isElementSelected(props.selectedElements, element.id))
            ),
        [props.elements, props.selectedElements]
    );

    const sortedSelectedElements = useMemo(() => sortElementsByName(props.selectedElements), [props.selectedElements]);

    const openElementListHandler = () => {
        if (triggerRef.current) {
            const { top } = triggerRef.current.getBoundingClientRect();
            setDropdownPlacement(top >= window.innerHeight * 0.7 ? 'top' : 'bottom');
        }

        setShowElementList(true);
    };

    const selectElementsHandler = (elements: Array<Element<T>>): void => {
        const newElements = elements.filter(element => !isElementSelected(props.selectedElements, element.id));

        if (newElements.length > 0) {
            props.changeSelection(sortElementsByName([...props.selectedElements, ...newElements]));
        }

        setShowElementList(false);
    };

    const deleteHandler = (deletedElement: Element<T>) => {
        props.changeSelection(props.selectedElements.filter(element => element.id !== deletedElement.id));
    };

    return (
        <div className={styles.wrapper}>
            {props.title && <h3 className={styles.title}>{props.title}</h3>}
            <div
                id={props.id}
                className={clsx(styles.container, props.title && styles.containerWithTitle)}
            >
                {sortedSelectedElements.map((element, index) => (
                    <div
                        key={String(element.id)}
                        className={styles.selectedItem}
                    >
                        <ChipItem
                            id={props.id ? `${props.id}_${index}` : undefined}
                            name={element.name}
                            img={element.img}
                            onDelete={() => deleteHandler(element)}
                        />
                    </div>
                ))}
                {availableElements.length > 0 && (
                    <div
                        ref={triggerRef}
                        className={styles.triggerWrapper}
                    >
                        <AddElementButton
                            id={props.id ? `${props.id}_add` : undefined}
                            hasSelectedElements={sortedSelectedElements.length > 0}
                            onClick={openElementListHandler}
                        />
                        {showElementList && (
                            <ElementDropdown
                                id={props.id ? `${props.id}_dropdown` : undefined}
                                elements={availableElements}
                                searchLabel={searchLabel}
                                allLabel={allLabel}
                                placement={dropdownPlacement}
                                anchorRef={triggerRef}
                                onSelect={selectElementsHandler}
                                onClose={() => setShowElementList(false)}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ChipSelector;
